import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";

type I = {
  id: string;
  trigger_type: string;
  trigger_text: string | null;
  response_text: string;
  e2e_ttfb_ms: number | null;
  llm_model: string | null;
  created_at: string;
};

export function InterventionStream({ sessionId, max = 20 }: { sessionId: string | null; max?: number }) {
  const [items, setItems] = useState<I[]>([]);

  useEffect(() => {
    if (!sessionId) { setItems([]); return; }
    let cancelled = false;
    void supabase
      .from("jarvis_interventions")
      .select("id, trigger_type, trigger_text, response_text, e2e_ttfb_ms, llm_model, created_at")
      .eq("session_id", sessionId)
      .order("created_at", { ascending: false })
      .limit(max)
      .then(({ data }) => { if (!cancelled && data) setItems(data.reverse() as I[]); });

    const ch = supabase
      .channel(`jarvis-interventions-${sessionId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "jarvis_interventions", filter: `session_id=eq.${sessionId}` }, (payload) => {
        setItems((prev) => [...prev.slice(-(max - 1)), payload.new as I]);
      })
      .subscribe();
    return () => { cancelled = true; void supabase.removeChannel(ch); };
  }, [sessionId, max]);

  if (!sessionId || items.length === 0) {
    return <p className="text-xs text-muted-foreground italic">Sem intervenções ainda.</p>;
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-2 pr-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm">
            <div className="flex items-center justify-between mb-1">
              <Badge variant="default" className="text-[10px] uppercase tracking-wider gap-1">
                <Sparkles className="w-3 h-3" /> {it.trigger_type}
              </Badge>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {it.e2e_ttfb_ms ? `${it.e2e_ttfb_ms}ms` : "—"} · {it.llm_model ?? "?"}
              </span>
            </div>
            <div className="text-foreground leading-relaxed">{it.response_text}</div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
